import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Box, CssBaseline, Fab, IconButton, Portal, ThemeProvider, Tooltip, Typography } from "@mui/material";
import { SessionAppBar, SESSION_APPBAR_TOOLBAR_PX } from "./SessionAppBar";
import { ProfileModal } from "./ProfileModal";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ScreenRotationIcon from "@mui/icons-material/ScreenRotation";
import { appTheme } from "../themes/themes";
import { Login } from "./Login";
import { useCallback, useEffect, useRef, useState } from "react";
import { usePostHog } from "@posthog/react";
import { useUser, type ActiveRoomInfo } from "../context/UserContext";
import {
  Connection,
  GameSnapshotData,
  LiteratureStateData,
  LobbyReadyData,
  MatchPlayerNetPayload,
  MatchDealIntroStateData,
  MatchStateData,
  ProposedPlayer,
  ReconnectFailedData,
  ReconnectSuccessData,
  RummyStateData,
  SetRecordPayload,
  SessionEndedData,
  StartGameData,
  TeamsUpdatedData,
} from "../sockets-client";
import { SocketEvent } from "../interface/SocketEvents";
import { RoomSelection } from "./RoomSelection";
import { Toast } from "./Toast";
import { Spinner } from "./Spinner";
import { config } from "../endpoints.config";
import { NamespaceSelector } from "./NamespaceSelector";
import {
  AvailableGamesResult,
  JoinRoomSuccessResult,
  ServerErrorResponse,
  ServerSuccessResponse,
} from "../interface/SocketResponse";
import { useCookies } from "react-cookie";
import { addHours } from "../utils";
import { CardDTO, PlayerInfo, TeamId } from "../interface/GameTypes";
import { GameTable } from "./GameTable";
import { LiteratureTable } from "./LiteratureTable";
import { RummyTable } from "./RummyTable";
import { TeamSetupScreen } from "./TeamSetupScreen";
import { LeaderboardDialog } from "./LeaderboardDialog";
import type { LeaderboardGame } from "../utils/apiBase";
import { teamAssignmentKey } from "../utils/teamAssignmentKey";
import { RulesModal } from "./RulesModal";
import { SeatHeldByOtherPlayerModal } from "./SeatHeldByOtherPlayerModal";
import { isSeatHeldByOtherPlayerError } from "../interface/socketErrorCodes";
import { DemoReactionsPage } from "./DemoReactionsPage";
import { buildJoinPath, parseJoinPath } from "../utils/joinPath";
import { ensureServerAwake } from "../utils/wakeServer";
import { clearStoredPlayerUuid, getStoredPlayerUuid, setStoredPlayerUuid } from "../utils/playerIdentityStorage";
import { PostGameGuestSignInDialog } from "./PostGameGuestSignInDialog";

function isDemoRoutePath(): boolean {
  if (typeof window === "undefined") return false;
  const p = window.location.pathname;
  return p === "/demo";
}

function initialInviteRoomId(): string | null {
  if (typeof window === "undefined") return null;
  if (isDemoRoutePath()) return null;
  return parseJoinPath(window.location.pathname)?.roomId ?? null;
}

function initialNamespace(): string | null {
  if (typeof window === "undefined") return null;
  if (isDemoRoutePath()) return null;
  const j = parseJoinPath(window.location.pathname);
  if (j) return j.namespace;
  return sessionStorage.getItem("addata_namespace");
}

function isInviteRoomMissingServerMessage(message: string): boolean {
  const m = message.trim();
  return /no room found/i.test(m) || /^room not found\.?$/i.test(m);
}

const styles = {
  background: {
    // Background is set on document.body (see useEffect below) so that
    // background-attachment:fixed works correctly.  Setting it here alongside
    // overflow-y:auto causes the browser to blank the background on many
    // Chromium builds (the "black screen" bug).
    minHeight: "100vh",
    width: "100%",
    maxWidth: "100vw",
    overflowX: "hidden" as const,
    overflowY: "auto" as const,
    display: "block",
    boxSizing: "border-box" as const,
  },
};

export const App = () => {
  const posthog = usePostHog();
  const posthogRef = useRef(posthog);
  posthogRef.current = posthog;

  const {
    loading: authLoading,
    user,
    signOut,
    token,
    activeRooms,
    isFirstLogin,
    refreshProfile,
    refreshAuthToken,
  } = useUser();
  const [, setSocketConnection] = useState<Connection | null>(null);
  const [nameSpaceSocketConnection, setNameSpaceSocketConnection] = useState<Connection | null>(null);
  // sessionStorage keys are tab-local: each browser tab gets its own identity,
  // which prevents same-browser multi-tab UUID collisions.
  const [currentNamespace, setCurrentNamespace] = useState<string | null>(initialNamespace);
  const [games, setGames] = useState<AvailableGamesResult[]>([]);
  const [bootstrapSocketError, setBootstrapSocketError] = useState<string | null>(null);
  const [serverWaking, setServerWaking] = useState(false);
  const [serverWakeError, setServerWakeError] = useState<string | null>(null);
  /** Room id from /join/.../... URL — auto-join after name entry. */
  const [inviteRoomId, setInviteRoomId] = useState<string | null>(initialInviteRoomId);
  /** Room id after host creates a room (from server roomID event). */
  const [hostedRoomId, setHostedRoomId] = useState<string | null>(null);

  const nsRef = useRef<string | null>(null);
  const joinInvitedForUrlRef = useRef(false);
  const inviteJoinPendingRef = useRef(false);
  const inviteJoinRetriedInitRef = useRef(false);
  const inviteRoomIdRef = useRef<string | null>(null);
  const goToGamePickerAfterLeaveRef = useRef(false);
  const didStartGameRef = useRef(false);
  const tokenRef = useRef<string | null>(null);
  const [guestPostGameSignInOpen, setGuestPostGameSignInOpen] = useState(false);
  /** Set when joining via /join/... fails so we leave the spinner and show home + message. */
  const [inviteLinkJoinBanner, setInviteLinkJoinBanner] = useState<string | null>(null);
  const didJoinRoomRef = useRef(false);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    if (token) setGuestPostGameSignInOpen(false);
  }, [token]);

  useEffect(() => {
    nsRef.current = currentNamespace;
  }, [currentNamespace]);

  useEffect(() => {
    inviteRoomIdRef.current = inviteRoomId;
  }, [inviteRoomId]);

  // After auth sign-in, persist display name for the table.
  // Do not gate on `currentNamespace`: namespace is often non-null from sessionStorage or /join/... before auth finishes,
  // and skipping here leaves the UI greeting from `user.displayName` while the socket never gets `initPlayer`.
  useEffect(() => {
    if (authLoading || !user?.displayName?.trim()) return;
    const stored = sessionStorage.getItem("addata_name");
    if (stored && stored.trim().length > 0) return;
    const name = user.displayName.trim();
    setPlayerName(name);
    sessionStorage.setItem("addata_name", name);
  }, [authLoading, user?.displayName]);

  const [didJoinRoom, setDidJoinRoom] = useState(false);
  const [didStartGame, setDidStartGame] = useState(false);
  /** During play the session bar starts hidden; user opens it with the corner control. */
  const [gameSessionBarOpen, setGameSessionBarOpen] = useState(false);
  useEffect(() => {
    didStartGameRef.current = didStartGame;
  }, [didStartGame]);
  useEffect(() => {
    didJoinRoomRef.current = didJoinRoom;
  }, [didJoinRoom]);
  useEffect(() => {
    if (didStartGame) setGameSessionBarOpen(false);
  }, [didStartGame]);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("There was an error");
  const [seatHeldByOtherModalOpen, setSeatHeldByOtherModalOpen] = useState(false);
  const [seatHeldByOtherModalMessage, setSeatHeldByOtherModalMessage] = useState("");
  const [playerName, setPlayerName] = useState(
    () => sessionStorage.getItem("addata_name") ?? ""
  );
  const [roomID, setRoomID] = useState("");
  const [playerUUID, setPlayerUUID] = useState(() => getStoredPlayerUuid());
  const [playerJoinMessage, setPlayerJoinMessage] = useState("");
  const [allPlayers, setAllPlayers] = useState<PlayerInfo[]>([]);
  const [myHand, setMyHand] = useState<CardDTO[]>([]);
  /** Increments on each `cardsDealt` (Addata): 1 = first pack + shuffle once, 2+ = post-trump / redeal — deal+fan only. */
  const [handDealVersion, setHandDealVersion] = useState(0);
  const [numDecks, setNumDecks] = useState(1);
  const [dealerIndex, setDealerIndex] = useState(0);
  const [isLobbyReady, setIsLobbyReady] = useState(false);
  const [proposedPlayers, setProposedPlayers] = useState<ProposedPlayer[]>([]);
  const [hostUUID, setHostUUID] = useState<string>("");
  const [roomMaxPlayers, setRoomMaxPlayers] = useState(6);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  const [rotationHintDismissed, setRotationHintDismissed] = useState(false);
  const [lobbyMatchTeamPoints, setLobbyMatchTeamPoints] = useState({ A: 0, B: 0 });
  const [lobbyMatchGamesPlayed, setLobbyMatchGamesPlayed] = useState(0);
  const [lobbyMatchPlayerPoints, setLobbyMatchPlayerPoints] = useState<MatchPlayerNetPayload[]>([]);
  const [lobbySetRecord, setLobbySetRecord] = useState<SetRecordPayload>({
    teamAWins: 0,
    teamBWins: 0,
    draws: 0,
    setSize: 3,
    matchesPlayed: 0,
    setComplete: false,
  });
  /** Shown on Addata `GameTable` right after `startGame` when lineup changed in lobby. */
  const [tableTeamShuffleReveal, setTableTeamShuffleReveal] = useState<PlayerInfo[] | null>(null);
  const lastLobbyTeamKeyRef = useRef("");
  /** Counts `lobbyReady` events that followed an in-progress game (play again). Used so we do not flash on the first post-game lobby. */
  const postGameLobbyVisitsRef = useRef(0);
  /** Set in lobby when teams change; consumed on next Addata `startGame` to drive the table overlay. */
  const pendingTableShuffleRevealRef = useRef(false);
  const [pendingSnapshot, setPendingSnapshot] = useState<GameSnapshotData | null>(null);
  const [pendingDealIntro, setPendingDealIntro] = useState<MatchDealIntroStateData | null>(null);
  const [, setWaitingPlayers] = useState<string[]>([]);
  const [literatureState, setLiteratureState] = useState<LiteratureStateData | null>(null);
  const [rummyState, setRummyState] = useState<RummyStateData | null>(null);

  useEffect(() => {
    // Re-enable the hint for each new game session.
    if (didStartGame) setRotationHintDismissed(false);
  }, [didStartGame]);
  useEffect(() => {
    if (!didStartGame || rotationHintDismissed) return;
    const t = window.setTimeout(() => setRotationHintDismissed(true), 5000);
    return () => window.clearTimeout(t);
  }, [didStartGame, rotationHintDismissed]);

  const [, setCookie] = useCookies([
    "namespace",
    "roomID",
    "uuid",
    "expirationTime",
    "name",
  ]);

  // Keep a consistent dark app shell across game picker/lobby screens.
  useEffect(() => {
    const b = document.body.style;
    b.backgroundColor = "#05070a";
    b.backgroundImage = "none";
    return () => {
      b.backgroundColor = "";
      b.backgroundImage = "";
    };
  }, []);

  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);
  const retryBootstrapWake = useCallback(() => {
    setBootstrapAttempt((n) => n + 1);
  }, []);

  // Wake ECS if needed, then bootstrap socket for availableGames.
  useEffect(() => {
    let cancelled = false;
    let conn: Connection | null = null;

    (async () => {
      setServerWaking(true);
      setServerWakeError(null);
      setBootstrapSocketError(null);
      try {
        await ensureServerAwake();
        if (cancelled) return;

        conn = new Connection(config.WebSocket);
        conn.listenForAvailableGames((data: AvailableGamesResult[]) => {
          setGames(data);
          setBootstrapSocketError(null);
          setServerWakeError(null);
        });
        conn.listenForConnectError((err) => {
          posthogRef.current?.capture("socket_bootstrap_connect_error", {
            message: err.message,
            ws_url: config.WebSocket,
          });
          posthogRef.current?.captureException(err instanceof Error ? err : new Error(String(err)), {
            scope: "socket_bootstrap",
          });
          setBootstrapSocketError(
            `Cannot reach game server (${err.message}). Tap Retry to start the server and connect again.`
          );
        });
        setSocketConnection(conn);
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : String(e);
          setServerWakeError(msg);
          setBootstrapSocketError(msg);
        }
      } finally {
        if (!cancelled) setServerWaking(false);
      }
    })();

    return () => {
      cancelled = true;
      conn?.removeListener("availableGames");
      conn?.removeConnectErrorListener();
      conn?.disconnect();
    };
  }, [bootstrapAttempt]);

  // Persist namespace from invite URL into sessionStorage
  useEffect(() => {
    const j = parseJoinPath(window.location.pathname);
    if (j) {
      sessionStorage.setItem("addata_namespace", j.namespace);
    }
  }, []);

  // Auto-join when user opened /join/<game>/<roomId> and entered their name
  useEffect(() => {
    if (
      !inviteRoomId ||
      !nameSpaceSocketConnection ||
      playerName.length === 0 ||
      didJoinRoom ||
      joinInvitedForUrlRef.current
    ) {
      return;
    }
    joinInvitedForUrlRef.current = true;
    inviteJoinPendingRef.current = true;
    inviteJoinRetriedInitRef.current = false;
    const name = (sessionStorage.getItem("addata_name") ?? playerName).trim();
    const uuid = getStoredPlayerUuid();
    nameSpaceSocketConnection.initPlayer(name, uuid, token);
    const rid = inviteRoomId;
    window.setTimeout(() => {
      nameSpaceSocketConnection.joinRoom(rid);
    }, 50);
  }, [inviteRoomId, nameSpaceSocketConnection, playerName, playerUUID, didJoinRoom, token]);

  // Persist cookies when values change
  useEffect(() => { setCookie("roomID", roomID, { path: "/" }); }, [roomID, setCookie]);
  useEffect(() => { setCookie("uuid", playerUUID, { path: "/" }); }, [playerUUID, setCookie]);
  useEffect(() => { setCookie("name", playerName.toLowerCase(), { path: "/" }); }, [playerName, setCookie]);

  // Connect to game namespace when selected (wake ECS first if scaled to zero)
  useEffect(() => {
    if (currentNamespace === null) {
      setNameSpaceSocketConnection((prev) => {
        prev?.disconnect();
        return null;
      });
      return;
    }

    let cancelled = false;
    let conn: Connection | null = null;

    (async () => {
      try {
        await ensureServerAwake();
        if (cancelled) return;

        conn = new Connection(config.WebSocket + currentNamespace);
        setNameSpaceSocketConnection(conn);
        sessionStorage.setItem("addata_namespace", currentNamespace);
        setCookie("namespace", currentNamespace, { path: "/" });
        const storedName = sessionStorage.getItem("addata_name") ?? "";
        const storedUUID = getStoredPlayerUuid();
        if (storedName.length > 0) {
          conn.initPlayer(storedName, storedUUID, token);
        }
        conn.listenForReconnect(() => {
          const sn = sessionStorage.getItem("addata_name") ?? "";
          const su = getStoredPlayerUuid();
          if (sn.length > 0 && su.length > 0) {
            conn?.initPlayer(sn, su, token);
          }
        });
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : String(e);
          setServerWakeError(msg);
          setBootstrapSocketError(msg);
        }
      }
    })();

    return () => {
      cancelled = true;
      conn?.removeReconnectListener();
      conn?.disconnect();
    };
  }, [currentNamespace, setCookie, token]);

  // Namespace connection only calls `initPlayer` on open; if auth or Google display name arrives later, register the name.
  useEffect(() => {
    if (!nameSpaceSocketConnection) return;
    const sessionName = (sessionStorage.getItem("addata_name") ?? "").trim();
    const fromUser = !authLoading ? (user?.displayName ?? "").trim() : "";
    const name = sessionName || playerName.trim() || fromUser;
    if (!name) return;
    const uuid = getStoredPlayerUuid();
    nameSpaceSocketConnection.initPlayer(name, uuid, token);
  }, [nameSpaceSocketConnection, authLoading, playerName, user?.displayName, token]);

  const clearRoomAndInviteState = useCallback(() => {
    setGuestPostGameSignInOpen(false);
    clearStoredPlayerUuid();
    setPlayerUUID("");
    setDidJoinRoom(false);
    setDidStartGame(false);
    setIsLobbyReady(false);
    setRoomID("");
    setMyHand([]);
    setAllPlayers([]);
    setProposedPlayers([]);
    setHostUUID("");
    setPendingSnapshot(null);
    setWaitingPlayers([]);
    setLiteratureState(null);
    setRummyState(null);
    setHostedRoomId(null);
    setInviteRoomId(null);
    setInviteLinkJoinBanner(null);
    setRoomMaxPlayers(6);
    joinInvitedForUrlRef.current = false;
    inviteJoinPendingRef.current = false;
    lastLobbyTeamKeyRef.current = "";
    postGameLobbyVisitsRef.current = 0;
    pendingTableShuffleRevealRef.current = false;
    setTableTeamShuffleReveal(null);
    setLobbyMatchTeamPoints({ A: 0, B: 0 });
    setLobbyMatchGamesPlayed(0);
    setLobbyMatchPlayerPoints([]);
  }, []);

  const dismissTableTeamShuffleReveal = useCallback(() => setTableTeamShuffleReveal(null), []);

  const applyGamePickerNamespace = useCallback(() => {
    sessionStorage.removeItem("addata_namespace");
    setCurrentNamespace(null);
    setCookie("namespace", "", { path: "/" });
  }, [setCookie]);

  const handleReturnToLobby = useCallback(() => {
    clearRoomAndInviteState();
    if (goToGamePickerAfterLeaveRef.current) {
      goToGamePickerAfterLeaveRef.current = false;
      applyGamePickerNamespace();
    }
    window.history.replaceState(null, "", "/");
  }, [clearRoomAndInviteState, applyGamePickerNamespace]);

  const resetToGamePicker = useCallback(() => {
    clearRoomAndInviteState();
    applyGamePickerNamespace();
    window.history.replaceState(null, "", "/");
  }, [clearRoomAndInviteState, applyGamePickerNamespace]);

  const handleGoToGamePicker = useCallback(() => {
    if (didJoinRoom && nameSpaceSocketConnection) {
      goToGamePickerAfterLeaveRef.current = true;
      try {
        nameSpaceSocketConnection.leaveRoom();
      } catch {
        goToGamePickerAfterLeaveRef.current = false;
        resetToGamePicker();
      }
      return;
    }
    resetToGamePicker();
  }, [didJoinRoom, nameSpaceSocketConnection, resetToGamePicker]);

  const handleLogout = useCallback(() => {
    goToGamePickerAfterLeaveRef.current = false;
    signOut();
    sessionStorage.removeItem("addata_name");
    setPlayerName("");
    resetToGamePicker();
  }, [signOut, resetToGamePicker]);

  const handleLeaveCurrentGame = useCallback(() => {
    if (!didJoinRoom || !nameSpaceSocketConnection) {
      handleReturnToLobby();
      return;
    }
    goToGamePickerAfterLeaveRef.current = false;
    try {
      nameSpaceSocketConnection.leaveRoom();
    } catch {
      handleReturnToLobby();
    }
  }, [didJoinRoom, nameSpaceSocketConnection, handleReturnToLobby]);

  // Register namespace socket listeners
  useEffect(() => {
    if (!nameSpaceSocketConnection) return;

    nameSpaceSocketConnection.listenForRoomId((data) => {
      setHostedRoomId(data.id);
      const ns = nsRef.current;
      if (ns) {
        window.history.replaceState(null, "", buildJoinPath(ns, data.id));
      }
      // Creator is not in the room until joinRoom runs; re-send init then join so name is never stale vs. create.
      const name = (sessionStorage.getItem("addata_name") ?? "").trim();
      const uuid = getStoredPlayerUuid();
      if (name) nameSpaceSocketConnection.initPlayer(name, uuid, token);
      window.setTimeout(() => nameSpaceSocketConnection.joinRoom(data.id), 50);
    });

    nameSpaceSocketConnection.listenForJoinRoomSuccess((data: JoinRoomSuccessResult) => {
      posthogRef.current?.capture("socket_join_room_success", {
        room_id: data.roomId,
        namespace: nsRef.current ?? undefined,
        existing_players: (data.existingPlayerNames ?? []).length,
      });
      inviteJoinPendingRef.current = false;
      inviteJoinRetriedInitRef.current = false;
      setRoomID(data.roomId);
      setHostedRoomId(null);
      setPlayerUUID(data.playerUUID);
      setStoredPlayerUuid(data.playerUUID);
      if (typeof data.maxPlayers === "number") setRoomMaxPlayers(data.maxPlayers);
      setCookie("expirationTime", addHours(new Date(), 2).toISOString(), { path: "/" });
      setDidJoinRoom(true);
      // Read name from sessionStorage to avoid stale closure (playerName state
      // may be "" if the user entered their name after the namespace socket
      // was created and this effect ran).
      const myName = sessionStorage.getItem("addata_name") ?? "";
      setWaitingPlayers([...(data.existingPlayerNames ?? []), ...(myName ? [myName] : [])]);

      // First human in the room: show team lobby immediately; server sends lobbyReady right after.
      const othersAlreadyIn = (data.existingPlayerNames ?? []).length > 0;
      if (!othersAlreadyIn) {
        setHostUUID(data.playerUUID);
        setIsLobbyReady(true);
        const selfRow: ProposedPlayer = {
          uuid: data.playerUUID,
          name: myName.trim() || null,
          seatIndex: 0,
          teamId: TeamId.A,
          isBot: false,
        };
        setProposedPlayers([selfRow]);
        lastLobbyTeamKeyRef.current = teamAssignmentKey([selfRow]);
      }
    });

    nameSpaceSocketConnection.listenForLobbyReady((data: LobbyReadyData) => {
      posthogRef.current?.capture("socket_lobby_ready", {
        namespace: nsRef.current ?? undefined,
        player_count: data.players?.length ?? 0,
        host_uuid: data.hostUUID,
      });
      const fromGame = didStartGameRef.current;
      const ns = nsRef.current ?? "";
      const rummyTable = ns === "/rummy";
      const prevKey = lastLobbyTeamKeyRef.current;
      const nextKey = teamAssignmentKey(data.players);

      if (!fromGame) {
        lastLobbyTeamKeyRef.current = nextKey;
      } else {
        postGameLobbyVisitsRef.current += 1;
        if (
          !rummyTable &&
          prevKey.length > 0 &&
          nextKey !== prevKey &&
          postGameLobbyVisitsRef.current > 1
        ) {
          pendingTableShuffleRevealRef.current = true;
        }
        lastLobbyTeamKeyRef.current = nextKey;
      }

      setProposedPlayers(data.players);
      setHostUUID(data.hostUUID);
      if (typeof data.maxPlayers === "number") setRoomMaxPlayers(data.maxPlayers);
      setLobbyMatchTeamPoints(
        data.matchTeamPoints ? { A: data.matchTeamPoints.A, B: data.matchTeamPoints.B } : { A: 0, B: 0 }
      );
      setLobbyMatchGamesPlayed(typeof data.matchGamesPlayed === "number" ? data.matchGamesPlayed : 0);
      setLobbyMatchPlayerPoints(data.matchPlayerPoints ?? []);
      if (data.setRecord) setLobbySetRecord(data.setRecord);
      setIsLobbyReady(true);
      // Handles both initial lobby and the post-game "play again" return to lobby
      setDidStartGame(false);
      setPendingDealIntro(null);
      setLiteratureState(null);
      setRummyState(null);
      if (fromGame && !tokenRef.current) {
        setGuestPostGameSignInOpen(true);
      }
    });

    nameSpaceSocketConnection.listenForTeamsUpdated((data: TeamsUpdatedData) => {
      posthogRef.current?.capture("socket_teams_updated", {
        namespace: nsRef.current ?? undefined,
        player_count: data.players?.length ?? 0,
      });
      const ns = nsRef.current ?? "";
      const rummyTable = ns === "/rummy";
      const prevKey = lastLobbyTeamKeyRef.current;
      const nextKey = teamAssignmentKey(data.players);
      if (
        !rummyTable &&
        prevKey.length > 0 &&
        nextKey !== prevKey &&
        postGameLobbyVisitsRef.current >= 1
      ) {
        pendingTableShuffleRevealRef.current = true;
      }
      lastLobbyTeamKeyRef.current = nextKey;
      setProposedPlayers(data.players);
      setHostUUID(data.hostUUID);
      if (typeof data.maxPlayers === "number") setRoomMaxPlayers(data.maxPlayers);
      setLobbyMatchTeamPoints(
        data.matchTeamPoints ? { A: data.matchTeamPoints.A, B: data.matchTeamPoints.B } : { A: 0, B: 0 }
      );
      setLobbyMatchGamesPlayed(typeof data.matchGamesPlayed === "number" ? data.matchGamesPlayed : 0);
      setLobbyMatchPlayerPoints(data.matchPlayerPoints ?? []);
      if (data.setRecord) setLobbySetRecord(data.setRecord);
      setIsLobbyReady(true);
      setPendingDealIntro(null);
    });

    nameSpaceSocketConnection.listenForStartGame((data: StartGameData) => {
      posthogRef.current?.capture("socket_start_game", {
        namespace: nsRef.current ?? undefined,
        player_count: data.players?.length ?? 0,
        num_decks: data.numDecks,
        dealer_index: data.dealerIndex,
        literature: Boolean(data.literature),
        rummy: Boolean(data.rummy),
      });
      const ns = nsRef.current ?? "";
      const addataTable = ns === "/addata" && !data.literature && !data.rummy;
      const pendingShuffle = pendingTableShuffleRevealRef.current;
      pendingTableShuffleRevealRef.current = false;
      if (pendingShuffle && addataTable) {
        setTableTeamShuffleReveal(data.players);
      } else {
        setTableTeamShuffleReveal(null);
      }
      setAllPlayers(data.players);
      setNumDecks(data.numDecks);
      setDealerIndex(data.dealerIndex);
      setHandDealVersion(0);
      setIsLobbyReady(false);
      setDidStartGame(true);
      setPendingDealIntro(data.matchDealIntroActive ? data.matchDealIntroState ?? null : null);
      setWaitingPlayers([]);
      if (data.literature) {
        setLiteratureState(null);
      }
      if (data.rummy) {
        setRummyState(null);
      }
    });

    nameSpaceSocketConnection.listenForLiteratureState((data: LiteratureStateData) => {
      setLiteratureState(data);
    });

    nameSpaceSocketConnection.listenForRummyState((data: RummyStateData) => {
      setRummyState(data);
    });

    nameSpaceSocketConnection.listenForCardsDealt((data) => {
      posthogRef.current?.capture("socket_cards_dealt", {
        namespace: nsRef.current ?? undefined,
        hand_size: data.hand?.length ?? 0,
      });
      setMyHand(data.hand);
      const ns = nsRef.current;
      if (ns !== "/literature" && ns !== "/rummy") {
        setHandDealVersion((v) => v + 1);
      }
    });

    nameSpaceSocketConnection.listenForPlayerJoining((data: ServerSuccessResponse) => {
      posthogRef.current?.capture("socket_player_joining", {
        namespace: nsRef.current ?? undefined,
        message: data.message,
      });
      setPlayerJoinMessage(data.message);
      // Extract player name from "X has joined!" message and add to waiting list
      const joined = data.message.replace(" has joined!", "");
      if (joined.length > 0) {
        setWaitingPlayers((prev) => [...prev, joined]);
      }
    });

    nameSpaceSocketConnection.listenForError((data: ServerErrorResponse) => {
      const msg = data.message ?? "";
      if (isSeatHeldByOtherPlayerError(data)) {
        posthogRef.current?.capture("socket_seat_held_by_other_player", {
          namespace: nsRef.current ?? undefined,
          code: data.code,
        });
        inviteJoinPendingRef.current = false;
        joinInvitedForUrlRef.current = false;
        inviteJoinRetriedInitRef.current = false;
        setSeatHeldByOtherModalMessage(msg);
        setSeatHeldByOtherModalOpen(true);
        return;
      }
      posthogRef.current?.capture("socket_server_error", {
        namespace: nsRef.current ?? undefined,
        message: data.message,
      });
      posthogRef.current?.captureException(new Error(data.message), {
        source: "socket_server_error",
        namespace: nsRef.current ?? undefined,
      });
      if (
        inviteJoinPendingRef.current &&
        inviteRoomIdRef.current &&
        !inviteJoinRetriedInitRef.current &&
        /name/i.test(msg) &&
        /join|room/i.test(msg)
      ) {
        inviteJoinRetriedInitRef.current = true;
        const name = (sessionStorage.getItem("addata_name") ?? "").trim();
        const uuid = getStoredPlayerUuid();
        nameSpaceSocketConnection.initPlayer(name, uuid, token);
        window.setTimeout(() => {
          nameSpaceSocketConnection.joinRoom(inviteRoomIdRef.current!);
        }, 80);
        return;
      }

      const pendingInviteJoin = inviteJoinPendingRef.current && !didJoinRoomRef.current;
      if (pendingInviteJoin) {
        inviteJoinPendingRef.current = false;
        joinInvitedForUrlRef.current = false;
        inviteJoinRetriedInitRef.current = false;
        setInviteRoomId(null);
        setInviteLinkJoinBanner(
          isInviteRoomMissingServerMessage(msg)
            ? "No room exists for this link. The code may be wrong or the room may have ended. Check with your host, or sign in and start a new game from the home screen."
            : "Could not join this room. Check the link or ask your host for a new invite."
        );
        applyGamePickerNamespace();
        window.history.replaceState(null, "", "/");
        return;
      }

      setIsError(true);
      setErrorMessage(data.message);
      if (inviteJoinPendingRef.current) {
        inviteJoinPendingRef.current = false;
        joinInvitedForUrlRef.current = false;
        inviteJoinRetriedInitRef.current = false;
        // Stay on /join/... so "Sign in is required" from an old backend is not mistaken for the home gate.
        // User can refresh after restarting the API, or fix the room id.
      }
    });

    nameSpaceSocketConnection.listenForKickedFromRoom(() => {
      setGuestPostGameSignInOpen(false);
      inviteJoinPendingRef.current = false;
      joinInvitedForUrlRef.current = false;
      inviteJoinRetriedInitRef.current = false;
      setDidJoinRoom(false);
      setDidStartGame(false);
      setIsLobbyReady(false);
      setRoomID("");
      setHostedRoomId(null);
      setInviteRoomId(null);
      setMyHand([]);
      setAllPlayers([]);
      setProposedPlayers([]);
      setHostUUID("");
      setPendingSnapshot(null);
      setLiteratureState(null);
      setRummyState(null);
      applyGamePickerNamespace();
      setIsError(true);
      setErrorMessage("The host removed you from this room.");
      window.history.replaceState(null, "", "/");
    });

    nameSpaceSocketConnection.listenForReconnectSuccess((data: ReconnectSuccessData) => {
      posthogRef.current?.capture("socket_reconnect_success", {
        namespace: nsRef.current ?? undefined,
        room_id: data.roomId,
        game_started: Boolean(data.gameStarted),
      });
      inviteJoinPendingRef.current = false;
      inviteJoinRetriedInitRef.current = false;
      setRoomID(data.roomId);
      setPlayerUUID(data.playerUUID);
      setStoredPlayerUuid(data.playerUUID);
      if (typeof data.maxPlayers === "number") setRoomMaxPlayers(data.maxPlayers);
      setDidJoinRoom(true);
      if (data.gameStarted && data.players) {
        setAllPlayers(data.players);
        setNumDecks(data.numDecks);
        setDealerIndex(data.dealerIndex);
        setMyHand(data.myHand);
        setIsLobbyReady(false);
        setDidStartGame(true);
        if (data.rummy) {
          setRummyState(null);
        }
      } else if (!data.gameStarted && data.proposedPlayers) {
        setProposedPlayers(data.proposedPlayers);
        lastLobbyTeamKeyRef.current = teamAssignmentKey(data.proposedPlayers);
        if (data.hostUUID) setHostUUID(data.hostUUID);
        setLobbyMatchTeamPoints(
          data.matchTeamPoints ? { A: data.matchTeamPoints.A, B: data.matchTeamPoints.B } : { A: 0, B: 0 }
        );
        setLobbyMatchGamesPlayed(typeof data.matchGamesPlayed === "number" ? data.matchGamesPlayed : 0);
        setLobbyMatchPlayerPoints(data.matchPlayerPoints ?? []);
        if (data.setRecord) setLobbySetRecord(data.setRecord);
        setIsLobbyReady(true);
        setPendingDealIntro(null);
        setDidStartGame(false);
      }
    });

    nameSpaceSocketConnection.listenForReconnectFailed((data: ReconnectFailedData) => {
      posthogRef.current?.capture("socket_reconnect_failed", {
        namespace: nsRef.current ?? undefined,
        message: data.message,
      });
      posthogRef.current?.captureException(new Error(data.message), {
        source: "socket_reconnect_failed",
        namespace: nsRef.current ?? undefined,
      });
      inviteJoinPendingRef.current = false;
      joinInvitedForUrlRef.current = false;
      setInviteRoomId(null);
      setDidJoinRoom(false);
      setDidStartGame(false);
      setIsLobbyReady(false);
      setProposedPlayers([]);
      setRoomID("");
      setMyHand([]);
      setAllPlayers([]);
      setPendingSnapshot(null);
      setWaitingPlayers([]);
      setLiteratureState(null);
      setRummyState(null);
      setErrorMessage(data.message);
      setIsError(true);
    });

    // Register gameSnapshot HERE (at App level, not inside GameTable) so the
    // event is captured even before GameTable mounts after a reconnect.
    nameSpaceSocketConnection.listenForGameSnapshot((data: GameSnapshotData) => {
      posthogRef.current?.capture("socket_game_snapshot", {
        namespace: nsRef.current ?? undefined,
        phase: data.phase,
        trump_suit: data.trumpSuit ?? undefined,
        round_cards: data.currentRound?.length ?? 0,
      });
      setPendingSnapshot(data);
    });

    if (currentNamespace === "/addata") {
      nameSpaceSocketConnection.listenForMatchState((data: MatchStateData) => {
        setLobbyMatchTeamPoints({ A: data.matchTeamPoints.A, B: data.matchTeamPoints.B });
        setLobbyMatchGamesPlayed(data.matchGamesPlayed);
        if (data.matchPlayerPoints) {
          setLobbyMatchPlayerPoints(data.matchPlayerPoints);
        }
        if (data.setRecord) {
          setLobbySetRecord(data.setRecord);
        }
      });
      nameSpaceSocketConnection.listenForMatchDealIntroState((data: MatchDealIntroStateData) => {
        setPendingDealIntro(data);
      });
      nameSpaceSocketConnection.listenForSessionEnded((data: SessionEndedData) => {
        const msg = `Session ended — Set ${data.setRecord.matchesPlayed}/${data.setRecord.setSize}, Team A wins ${data.setRecord.teamAWins}, Team B wins ${data.setRecord.teamBWins}, draws ${data.setRecord.draws}.`;
        setIsError(true);
        setErrorMessage(msg);
      });
    }

    nameSpaceSocketConnection.listenForLeftRoom(() => {
      handleReturnToLobby();
    });

    return () => {
      nameSpaceSocketConnection.removeListener(SocketEvent.RoomID);
      nameSpaceSocketConnection.removeListener(SocketEvent.JoinRoomSuccess);
      nameSpaceSocketConnection.removeListener(SocketEvent.LobbyReady);
      nameSpaceSocketConnection.removeListener(SocketEvent.TeamsUpdated);
      nameSpaceSocketConnection.removeListener(SocketEvent.StartGame);
      nameSpaceSocketConnection.removeListener(SocketEvent.CardsDealt);
      nameSpaceSocketConnection.removeListener(SocketEvent.PlayerJoined);
      nameSpaceSocketConnection.removeListener(SocketEvent.Error);
      nameSpaceSocketConnection.removeListener(SocketEvent.ReconnectSuccess);
      nameSpaceSocketConnection.removeListener(SocketEvent.ReconnectFailed);
      nameSpaceSocketConnection.removeListener(SocketEvent.GameSnapshot);
      nameSpaceSocketConnection.removeListener(SocketEvent.LiteratureState);
      nameSpaceSocketConnection.removeListener(SocketEvent.RummyState);
      nameSpaceSocketConnection.removeListener(SocketEvent.LeftRoom);
      nameSpaceSocketConnection.removeListener(SocketEvent.KickedFromRoom);
      if (currentNamespace === "/addata") {
        nameSpaceSocketConnection.removeListener(SocketEvent.MatchState);
        nameSpaceSocketConnection.removeListener(SocketEvent.MatchDealIntroState);
        nameSpaceSocketConnection.removeListener(SocketEvent.SessionEnded);
      }
    };
  }, [
    nameSpaceSocketConnection,
    setCookie,
    handleReturnToLobby,
    currentNamespace,
    applyGamePickerNamespace,
    token,
  ]);

  const addPlayer = (value: string) => {
    setInviteLinkJoinBanner(null);
    setPlayerName(value);
    sessionStorage.setItem("addata_name", value);
    nameSpaceSocketConnection?.initPlayer(value, playerUUID, token);
  };

  const handleSelectNamespace = useCallback((endpoint: string) => {
    setInviteLinkJoinBanner(null);
    setCurrentNamespace(endpoint);
  }, []);

  const handleApplyTableNameFromProfile = useCallback(
    (name: string) => {
      if (didStartGame) return;
      setPlayerName(name);
      sessionStorage.setItem("addata_name", name);
      nameSpaceSocketConnection?.initPlayer(name, playerUUID, token);
    },
    [didStartGame, playerUUID, token, nameSpaceSocketConnection]
  );

  const handleResumeActiveRoom = useCallback(
    (room: ActiveRoomInfo) => {
      // New device/tab may have no addata_name yet; init/join requires name in session.
      const name =
        playerName.trim() ||
        user?.displayName?.trim() ||
        (typeof sessionStorage !== "undefined" ? sessionStorage.getItem("addata_name")?.trim() : "") ||
        "";
      if (name) sessionStorage.setItem("addata_name", name);
      sessionStorage.setItem("addata_namespace", room.namespace);
      setStoredPlayerUuid(room.playerUuid);
      setPlayerUUID(room.playerUuid);
      // Use deterministic join-by-room flow for resume to avoid reconnect race conditions.
      setDidJoinRoom(false);
      setDidStartGame(false);
      setIsLobbyReady(false);
      setRoomID("");
      setInviteRoomId(room.roomId);
      joinInvitedForUrlRef.current = false;
      inviteJoinPendingRef.current = false;
      inviteJoinRetriedInitRef.current = false;
      setCurrentNamespace(room.namespace);
      window.history.replaceState(null, "", buildJoinPath(room.namespace, room.roomId));
      void refreshProfile();
    },
    [refreshProfile, playerName, user?.displayName]
  );

  const myPlayerInfo = allPlayers.find((p) => p.uuid === playerUUID) ?? null;

  const getCurrentComponent = () => {
    if (window.location.pathname === "/demo") {
      return <DemoReactionsPage />;
    }
    if (!token) {
      if (authLoading) {
        return <Spinner color="secondary" headerText="Loading…" subText="" joinedPlayers={[]} />;
      }
      const invitePath = parseJoinPath(window.location.pathname);
      if (invitePath && playerName.length === 0) {
        return (
          <Login
            guestInviteMode
            titleText="Join game"
            buttonText="Continue as guest"
            subtitleText="Sign in with Google for your account, or enter a name to play as a guest."
            mode="landing"
            onFormSubmit={addPlayer}
            inviteLinkErrorText={inviteLinkJoinBanner}
          />
        );
      }
      if (!invitePath) {
        return (
          <Login
            titleText="Patta Adda"
            buttonText="Continue"
            subtitleText="Sign in to play Addata, Literature, and Rummy"
            mode="landing"
            onFormSubmit={addPlayer}
            inviteLinkErrorText={inviteLinkJoinBanner}
          />
        );
      }
    }
    if (currentNamespace === null) {
      if (playerName.length === 0) {
        if (authLoading) {
          return (
            <Spinner color="secondary" headerText="Loading…" subText="" joinedPlayers={[]} />
          );
        }
        return (
          <Login
            titleText="Patta Adda"
            buttonText="Continue"
            subtitleText="Play Addata, Literature, and Rummy with friends"
            mode="landing"
            onFormSubmit={addPlayer}
            inviteLinkErrorText={inviteLinkJoinBanner}
          />
        );
      }
      return (
        <NamespaceSelector
          games={games}
          displayNameForGreeting={(user?.displayName?.trim() || playerName).trim()}
          isFirstLogin={isFirstLogin}
          onFormSubmit={handleSelectNamespace}
          connectionError={bootstrapSocketError ?? serverWakeError}
          serverWaking={serverWaking}
          onRetryWake={retryBootstrapWake}
          activeRooms={activeRooms}
          showResumeColumn={Boolean(token)}
          onResumeRoom={handleResumeActiveRoom}
          onRefreshResume={refreshProfile}
          inviteJoinNotice={inviteLinkJoinBanner}
        />
      );
    }
    if (playerName.length === 0) {
      return (
        <Login
          titleText={inviteRoomId ? "What's your name?" : "Welcome"}
          buttonText="Continue"
          subtitleText={
            inviteRoomId
              ? "You're joining a room from a shared link. Enter the name others will see at the table."
              : undefined
          }
          mode="name"
          onFormSubmit={addPlayer}
          inviteLinkErrorText={inviteLinkJoinBanner}
        />
      );
    }
    if (inviteRoomId && !didJoinRoom && nameSpaceSocketConnection) {
      return (
        <Spinner
          color="secondary"
          headerText="Joining room…"
          subText={`Room ${inviteRoomId}`}
          joinedPlayers={[]}
        />
      );
    }
    if (!didJoinRoom) {
      if (nameSpaceSocketConnection !== null) {
        return (
          <RoomSelection
            playerName={playerName}
            socketConnection={nameSpaceSocketConnection}
            hostedRoomId={hostedRoomId}
            namespacePath={currentNamespace}
            onBack={handleGoToGamePicker}
            activeRooms={activeRooms}
            onResumeRoom={handleResumeActiveRoom}
            onRefreshResume={refreshProfile}
          />
        );
      }
    }
    if (didJoinRoom && !didStartGame && !isLobbyReady) {
      return (
        <Spinner
          color="secondary"
          headerText="Opening lobby…"
          subText={roomID ? `Room ${roomID}` : undefined}
          joinedPlayers={[]}
          inviteNamespace={currentNamespace}
          inviteRoomId={roomID}
        />
      );
    }

    if (didJoinRoom && !didStartGame && isLobbyReady && nameSpaceSocketConnection) {
      const lobbySeating = currentNamespace === "/rummy" ? "table" : "teams";
      return (
        <TeamSetupScreen
          proposedPlayers={proposedPlayers}
          myUUID={playerUUID}
          hostUUID={hostUUID}
          socketConnection={nameSpaceSocketConnection}
          roomID={roomID}
          seatingMode={lobbySeating}
          namespacePath={currentNamespace}
          roomMaxPlayers={roomMaxPlayers}
          matchTeamPoints={currentNamespace === "/addata" ? lobbyMatchTeamPoints : undefined}
          matchGamesPlayed={currentNamespace === "/addata" ? lobbyMatchGamesPlayed : undefined}
          matchPlayerPoints={currentNamespace === "/addata" ? lobbyMatchPlayerPoints : undefined}
          setRecord={currentNamespace === "/addata" ? lobbySetRecord : undefined}
        />
      );
    }
    if (didStartGame && currentNamespace === "/literature" && nameSpaceSocketConnection) {
      if (!literatureState || !myPlayerInfo) {
        return (
          <Spinner
            color="secondary"
            headerText="Starting Literature …"
            subText="Dealing cards"
            joinedPlayers={[]}
          />
        );
      }
      return (
        <LiteratureTable
          state={literatureState}
          myUUID={playerUUID}
          socketConnection={nameSpaceSocketConnection}
          onReturnToLobby={handleReturnToLobby}
        />
      );
    }
    if (didStartGame && currentNamespace === "/rummy" && nameSpaceSocketConnection) {
      if (!rummyState || !myPlayerInfo) {
        return (
          <Spinner
            color="secondary"
            headerText="Starting Rummy …"
            subText="Dealing cards"
            joinedPlayers={[]}
          />
        );
      }
      return (
        <RummyTable
          state={rummyState}
          myUUID={playerUUID}
          socketConnection={nameSpaceSocketConnection}
          onReturnToLobby={handleReturnToLobby}
        />
      );
    }
    if (didStartGame && myPlayerInfo) {
      return (
        <GameTable
          players={allPlayers}
          myPlayerInfo={myPlayerInfo}
          myHand={myHand}
          handDealVersion={handDealVersion}
          numDecks={numDecks}
          dealerIndex={dealerIndex}
          socketConnection={nameSpaceSocketConnection!}
          roomID={roomID}
          hostUUID={hostUUID}
          pendingSnapshot={pendingSnapshot}
          onSnapshotConsumed={() => setPendingSnapshot(null)}
          onReturnToLobby={handleReturnToLobby}
          tableTeamShuffleReveal={currentNamespace === "/addata" ? tableTeamShuffleReveal : null}
          onTableTeamShuffleDismiss={dismissTableTeamShuffleReveal}
          signedIn={Boolean(token)}
          initialDealIntro={currentNamespace === "/addata" ? pendingDealIntro : null}
        />
      );
    }
    return null;
  };

  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const showSessionBar = pathname !== "/demo" && playerName.length > 0;
  /** During play, bar can be collapsed; peek = collapsed with no AppBar. */
  const gameSessionBarPeekOnly = showSessionBar && didStartGame && !gameSessionBarOpen;
  const showExpandedSessionBar = showSessionBar && !gameSessionBarPeekOnly;
  const gameSessionMenuToggle = showSessionBar && didStartGame;
  const sessionDisplayName = (user?.displayName?.trim() || playerName).trim();
  const showChooseGame =
    currentNamespace !== null || didJoinRoom || roomID.length > 0 || hostedRoomId !== null;
  const showRulesFab = currentNamespace === "/addata" && (didStartGame || (didJoinRoom && isLobbyReady));
  const leaderboardGame: LeaderboardGame =
    currentNamespace === "/literature" ? "literature" : currentNamespace === "/rummy" ? "rummy" : "addata";
  const postGameGuestGameLabel =
    currentNamespace === "/literature"
      ? "Literature"
      : currentNamespace === "/rummy"
        ? "Rummy"
        : currentNamespace === "/addata"
          ? "Addata"
          : undefined;

  return (
    <div style={styles.background}>
      <ThemeProvider theme={appTheme}>
        <CssBaseline enableColorScheme />
        {showExpandedSessionBar ? (
          <SessionAppBar
            displayName={sessionDisplayName}
            onOpenProfile={() => setProfileOpen(true)}
            onOpenLeaderboard={() => setLeaderboardOpen(true)}
            toolbarTrailingReservePx={didStartGame && gameSessionBarOpen ? 48 : 0}
          />
        ) : null}
        {/* Portal keeps position:fixed tied to the viewport; inside the scroll/root div some
            browsers treat fixed as relative to that box, which made the control sit by Logout. */}
        {gameSessionMenuToggle ? (
          <Portal>
            {/* Bottom-left: top-right sat on the sm sidebar (hand) and MUI Tooltip covered cards. */}
            <IconButton
              onClick={() => setGameSessionBarOpen((open) => !open)}
              aria-label={
                gameSessionBarOpen
                  ? "Hide menu bar"
                  : "Show menu bar: profile, all games, and log out"
              }
              size="small"
              sx={{
                position: "fixed",
                left: "max(12px, env(safe-area-inset-left, 0px))",
                bottom: "max(20px, env(safe-area-inset-bottom, 0px))",
                top: "auto",
                right: "auto",
                zIndex: 1400,
                width: 40,
                height: 40,
                color: "#fff",
                bgcolor: gameSessionBarOpen ? "rgba(8, 14, 10, 0.55)" : "rgba(8, 14, 10, 0.72)",
                border: "1px solid rgba(255, 193, 7, 0.25)",
                boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
                "&:hover": { bgcolor: "rgba(12, 22, 14, 0.88)" },
              }}
            >
              {gameSessionBarOpen ? (
                <ExpandLessRoundedIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownRoundedIcon fontSize="small" />
              )}
            </IconButton>
          </Portal>
        ) : null}
        <ProfileModal
          open={profileOpen}
          onClose={() => setProfileOpen(false)}
          user={user}
          authToken={token}
          tableDisplayName={playerName}
          onApplyTableName={handleApplyTableNameFromProfile}
          onAuthProfileUpdated={refreshAuthToken}
          tableNameEditable={!didStartGame}
          showChooseGame={showChooseGame}
          onChooseGame={handleGoToGamePicker}
          showLeaveGame={didStartGame}
          onLeaveGame={handleLeaveCurrentGame}
          onLogout={handleLogout}
        />
        <LeaderboardDialog
          open={leaderboardOpen}
          onClose={() => setLeaderboardOpen(false)}
          highlightUserId={user?.userId ?? null}
          initialGame={leaderboardGame}
        />
        {didStartGame && !rotationHintDismissed ? (
          <Box
            sx={{
              display: "none",
              "@media (orientation: portrait) and (max-width: 900px)": {
                display: "flex",
              },
              position: "fixed",
              top: "calc(env(safe-area-inset-top, 0px) + 8px)",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1400,
              width: "min(92vw, 420px)",
              backgroundColor: "rgba(10,13,18,0.86)",
              border: "1px solid rgba(255,255,255,0.16)",
              borderRadius: 2,
              boxShadow: "0 8px 26px rgba(0,0,0,0.42)",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              px: 1.25,
              py: 0.75,
              textAlign: "center",
              pointerEvents: "auto",
            }}
          >
            <IconButton
              size="small"
              aria-label="Dismiss rotate hint"
              onClick={() => setRotationHintDismissed(true)}
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                color: "rgba(255,255,255,0.75)",
                p: 0.5,
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <ScreenRotationIcon sx={{ fontSize: 20, color: "#FFD700" }} />
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.88rem", lineHeight: 1.25 }}>
              Rotate to landscape for best gameplay
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.62)", fontSize: "0.74rem", lineHeight: 1.2 }}>
              You can still continue in portrait mode.
            </Typography>
          </Box>
        ) : null}
        <Box
          sx={{
            pt: showExpandedSessionBar
              ? {
                  xs: `calc(${SESSION_APPBAR_TOOLBAR_PX.xs}px + env(safe-area-inset-top, 0px))`,
                  sm: `calc(${SESSION_APPBAR_TOOLBAR_PX.sm}px + env(safe-area-inset-top, 0px))`,
                }
              : 0,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            minHeight: "100vh",
            boxSizing: "border-box",
          }}
        >
          {getCurrentComponent()}
        </Box>
        {playerJoinMessage.length !== 0 && (
          <Toast
            message={playerJoinMessage}
            severity="info"
            onClose={() => setPlayerJoinMessage("")}
          />
        )}
        {isError && (
          <Toast
            message={errorMessage}
            severity="error"
            onClose={() => setIsError(false)}
          />
        )}
        {showRulesFab ? (
          <Tooltip title="Rules" placement="left">
            <Fab
              size="small"
              onClick={() => setRulesOpen(true)}
              sx={{
                position: "fixed",
                bottom: "max(20px, env(safe-area-inset-bottom, 0px))",
                right: "max(20px, env(safe-area-inset-right, 0px))",
                backgroundColor: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(6px)",
                color: "white",
                border: "1px solid rgba(255,255,255,0.3)",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.25)",
                },
                zIndex: 1300,
              }}
            >
              <HelpOutlineIcon />
            </Fab>
          </Tooltip>
        ) : null}
        <RulesModal
          open={rulesOpen}
          onClose={() => setRulesOpen(false)}
          variant={
            currentNamespace === "/literature"
              ? "literature"
              : currentNamespace === "/rummy"
                ? "rummy"
                : "addata"
          }
        />
        <SeatHeldByOtherPlayerModal
          open={seatHeldByOtherModalOpen}
          detailMessage={seatHeldByOtherModalMessage}
          onClose={() => {
            setSeatHeldByOtherModalOpen(false);
            setSeatHeldByOtherModalMessage("");
          }}
        />
        <PostGameGuestSignInDialog
          open={guestPostGameSignInOpen && !token}
          onClose={() => setGuestPostGameSignInOpen(false)}
          gameLabel={postGameGuestGameLabel}
        />
      </ThemeProvider>
    </div>
  );
};
